import {
  type Action,
  type Computed,
  FilterActionTypes,
  type Thunk,
  action,
  computed,
  thunk,
} from "easy-peasy";

import { Logger } from "../../dependencies/logger";
import { decodeRegularAvailabilityConfiguration } from "../../dependencies/utils";
import getRestaurantDetails, {
  type GetRestaurantDetailsParams,
  type RestaurantDetails,
} from "../../gql/getters/restaurantDetails";
import {
  AbortError,
  type LogEffect,
  createLogEffect,
  isAbortError,
} from "../../utils/store";
import { type WithRegularAvailabilityConfig } from "../types";

const logEffect = (
  property: keyof FilterActionTypes<ByIdModel>,
  enabled = false,
) => createLogEffect<ByIdModel>("BY_ID", property, enabled);

const logger = new Logger("RESTAURANT_STORE - BY_ID", "LOG");
// logger.enable();

/**
 * Per-id restaurant details.
 * - `undefined` — not resolved yet (blocks the contiguous page prefix)
 * - `null` — last fetch finished with no details (skip; do not block). Retried
 *   when `fetch` is invoked again for that id (filter refresh / page growth).
 * - details — success
 */
export type RestaurantDetailsById = Record<
  RestaurantDetails["id"],
  (RestaurantDetails & WithRegularAvailabilityConfig) | null
>;

export type ByIdMergePayload =
  RestaurantDetails[] | { id: number; value: null };

export type ByIdModel = {
  value: RestaurantDetailsById;
  valueOnChange: LogEffect<ByIdModel>;
  merge: Action<ByIdModel, ByIdMergePayload>;

  /**
   * In-flight `fetch` count. Page appends can leave a prefix-stable request
   * running while starting another; {@link loading} stays true until this hits 0.
   */
  activeFetchCount: number;
  /**
   * How many in-flight `fetch` batches include this id. Row loading must use
   * this, not {@link loading} — a map pin fetch would otherwise skeleton every
   * uncached list row.
   */
  inFlightById: Record<number, number>;
  beginFetch: Action<ByIdModel, number[]>;
  endFetch: Action<ByIdModel, number[]>;

  /** True while any `fetch` is in flight (`activeFetchCount > 0`). */
  loading: Computed<ByIdModel, boolean>;
  loadingOnChange: LogEffect<ByIdModel>;

  /**
   * In-flight fetch controllers. Page appends overlap, so this is an array —
   * unlike `allIds`, a new `fetch` must not abort siblings.
   */
  abortControllers: AbortController[];
  addAbortController: Action<ByIdModel, AbortController>;
  removeAbortController: Action<ByIdModel, AbortController>;
  /** Abort every in-flight `fetch`. Does not start a new one. */
  abort: Thunk<ByIdModel>;

  fetch: Thunk<
    ByIdModel,
    {
      ids: GetRestaurantDetailsParams["variables"]["id"][];
    }
  >;
};

const byIdModel: ByIdModel = {
  value: {},
  valueOnChange: logEffect("value"),
  merge: action((state, payload) => {
    if (!Array.isArray(payload)) {
      // Soft-fail must not clobber a newer success from an overlapping fetch.
      if (state.value[payload.id] !== undefined) {
        return;
      }
      state.value[payload.id] = null;
      return;
    }

    for (const restaurant of payload) {
      state.value[restaurant.id] = {
        ...restaurant,
        regularAvailabilityConfig: decodeRegularAvailabilityConfiguration(
          restaurant.configurationJSON,
        ),
      };
    }
  }),

  activeFetchCount: 0,
  inFlightById: {},
  beginFetch: action((state, ids) => {
    state.activeFetchCount += 1;
    for (const id of ids) {
      state.inFlightById[id] = (state.inFlightById[id] ?? 0) + 1;
    }
  }),
  endFetch: action((state, ids) => {
    state.activeFetchCount = Math.max(0, state.activeFetchCount - 1);
    for (const id of ids) {
      const next = (state.inFlightById[id] ?? 0) - 1;
      if (next <= 0) {
        delete state.inFlightById[id];
      } else {
        state.inFlightById[id] = next;
      }
    }
  }),

  loading: computed((state) => state.activeFetchCount > 0),
  loadingOnChange: logEffect("loading"),

  abortControllers: [],
  addAbortController: action((state, abortController) => {
    state.abortControllers.push(abortController);
  }),
  removeAbortController: action((state, abortController) => {
    state.abortControllers = state.abortControllers.filter(
      (controller) => controller !== abortController,
    );
  }),
  abort: thunk((_actions, _payload, { getState }) => {
    for (const controller of getState().abortControllers) {
      if (!controller.signal.aborted) {
        controller.abort(
          new AbortError('Canceled "byId.fetch" before re-fetching'),
        );
      }
    }
  }),

  fetch: thunk(async (actions, { ids }, { getState }) => {
    const { value } = getState();
    // Retry failed (`null`) ids when they are requested again.
    const missingIds = ids.filter((id) => value[id] == null);
    if (missingIds.length === 0) {
      logger.log("Skipping fetch", { ids });
      // Do not touch loading — a prefix-stable overlapping fetch may still be
      // in flight (page appends intentionally leave the prior request alive).
      return;
    }

    const abortController = new AbortController();
    const { signal: abortSignal } = abortController;
    actions.addAbortController(abortController);

    actions.beginFetch(missingIds);
    logger.log(`Fetching ${missingIds.length} ...`, { ids, missingIds });

    // handle aborts — settle exactly once (abort listener and post-await path)
    let aborted = false;
    let settled = false;
    function settle() {
      if (settled) {
        return;
      }
      settled = true;
      actions.endFetch(missingIds);
    }
    function onAbort() {
      logger.log(`Fetch aborted: "${abortSignal.reason}"`, { ids, missingIds });
      aborted = true;
      settle();
    }
    abortSignal.addEventListener("abort", onAbort);

    try {
      // Merge each restaurant as its request completes; the app layer reveals
      // rows in paged order so out-of-order responses cannot reshuffle the list.
      // Failed ids are stored as `null` so the loaded prefix can skip them.
      await Promise.allSettled(
        missingIds.map(async (id) => {
          try {
            const restaurant = await getRestaurantDetails({ id }, abortSignal);
            if (abortSignal.aborted || aborted) {
              return;
            }
            if (restaurant) {
              actions.merge([restaurant]);
              return;
            }
            actions.merge({ id, value: null });
          } catch (error) {
            if (abortSignal.aborted || aborted || isAbortError(error)) {
              return;
            }
            logger.log(`Fetch failed for #${id}`, { error });
            actions.merge({ id, value: null });
          }
        }),
      );
    } finally {
      abortSignal.removeEventListener("abort", onAbort);
      actions.removeAbortController(abortController);
    }

    if (abortSignal.aborted || aborted) {
      logger.log("Fetch aborted", { ids, missingIds });
      settle();
      return;
    }

    logger.log(`Fetch ${missingIds.length} done`, { ids, missingIds });
    settle();
  }),
};

export default byIdModel;
